import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Grid,
    Box,
    Container,
    Typography,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Apps, PostAdd, BallotOutlined, Euro } from '@mui/icons-material';
import Paper from '@mui/material/Paper';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SttValidatedForm from '../../../sporttia/SttValidatedForm';
import SCWall from './SCWall';
import { getArrayWithOnlyValues } from '../../../lib/utils';
import { SttEventCardList } from '../../../sporttia/public/cards/SttEventCardList';
import { getPath } from '../../Pages';
import logoEUNextGen from '../../../images/dipSevillaLogos/logoEUNextGeneration.png';
import logoPlanRecuperacion from '../../../images/dipSevillaLogos/logoPlanRecuperacion.png';
import logoDipuSevilla from '../../../images/dipSevillaLogos/logoDipuSevilla.png';
import logoGob from '../../../images/dipSevillaLogos/MPTMD.Gob.png';
import {
    AppContext,
    SttActivityCardList,
    SttButton,
    SttTabs,
} from '../../../sporttia/all';
import Page404 from '../../Page404';

export default function SCProfile() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const formActivity = useForm();
    const watchActivityKeyword = formActivity.watch('keyword');
    const formEvent = useForm();
    const watchEventKeyword = formEvent.watch('keyword');
    const [idSC, setIdSC] = useState<any>();
    const [sc, setSC] = useState<any>();
    const [SCPrivileges, setSCPrivileges] = useState<number[]>([]);
    const [searchKeywordEvent, setSearchKeywordEvent] = useState('');
    const [searchKeywordActivity, setSearchKeywordActivity] = useState('');

    const hasUserMship =
        cxt.user?.role === cxt.constants.roleUser ||
        cxt.user?.role === cxt.constants.roleTeacher;

    /**
     * Get sport centers privileges data
     */
    useEffect(() => {
        if (sc) {
            if (hasUserMship) {
                cxt.api('GET', `/scs/${sc.id}/me`, {
                    success: (r: any) => {
                        if (r?.mship?.privileges) {
                            setSCPrivileges(r.mship.privileges);
                        }
                    },
                });
            } else {
                cxt.api('GET', `/scs/${sc.id}/privileges`, {
                    success: (r: any) => {
                        const activePrivileges = r.rows.filter(
                            (privilege: any) => privilege.active,
                        );

                        setSCPrivileges(
                            getArrayWithOnlyValues(
                                'id',
                                activePrivileges,
                            ) as number[],
                        );
                    },
                });
            }
        }
    }, [sc]);

    /**
     * Sports center changes
     */
    useEffect(() => setIdSC(id), [id]);

    /**
     * Load sport center
     */
    useEffect(() => {
        if (idSC) {
            loadSC();
            cxt.setHeader({ title: cxt?.sc?.name ?? '' });
        }
    }, [idSC]);

    /**
     * Load SC
     */
    const loadSC = () => {
        cxt.api('GET', `/scs/${idSC}`, {
            success: (r: any) => {
                setSC(r.sc);
            },
        });
    };

    const hasPurse =
        sc?.modules?.find((name: any) => name === 'purse') && sc?.tpvConf;

    // -----| Render |-----
    if (sc?.status === 'ACTIVE') {
        return (
            <Container maxWidth="md">
                <Paper>
                    <Box p={2}>
                        <Grid container spacing={3} justifyContent="center">
                            <Grid size={{ md: 12 }}>
                                <Box mt={3} sx={{ textAlign: 'center' }}>
                                    <img
                                        src={cxt.getResource(
                                            `/images/${
                                                sc.logo?.id ?? 0
                                            }.jpg?outfit=100`,
                                        )}
                                        alt={`Logo de ${sc.name}`}
                                    />
                                </Box>

                                <Box mt={3} sx={{ textAlign: 'center' }}>
                                    <Typography variant="h5">
                                        {sc.name}
                                    </Typography>

                                    <Typography variant="subtitle1">
                                        {sc.city && sc.city.name}{' '}
                                        {sc.city &&
                                            sc.city.province &&
                                            `(${sc.city.province.name})`}
                                    </Typography>
                                </Box>
                                {sc?.isDipuSevilla && (
                                    <Box
                                        display="flex"
                                        alignItems="center" // This aligns items to the top
                                        justifyContent="center"
                                        gap={2} // Adds space between
                                    >
                                        <img
                                            src={logoEUNextGen}
                                            alt="Logo EU Next Generation"
                                            width="200px"
                                            height="52px"
                                        />
                                        <img
                                            src={logoGob}
                                            alt="Logo Gobierno de España Min. Transp, Mov, Ag Urb"
                                            width="200px"
                                            height="52px"
                                        />
                                        <img
                                            src={logoPlanRecuperacion}
                                            alt="Logo Plan de recuperación"
                                            width="200px"
                                            height="100px"
                                        />
                                        <img
                                            src={logoDipuSevilla}
                                            alt="Logo Diputación de Sevilla"
                                            width="52px"
                                            height="52px"
                                        />
                                    </Box>
                                )}
                                <Box mt={1} sx={{ textAlign: 'center' }}>
                                    <Button
                                        size="small"
                                        startIcon={<ContactSupportIcon />}
                                        onClick={() =>
                                            navigate(
                                                getPath('scContact', {
                                                    state: {
                                                        id: sc.id,
                                                    },
                                                }),
                                            )
                                        }
                                    >
                                        {t('contact.title')}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                        {sc.bulletin && (
                            <Box
                                mt={2}
                                p={2}
                                border={3}
                                borderColor="secondary.main"
                            >
                                {sc.bulletin}
                            </Box>
                        )}

                        <Box mt={3}>
                            {/* La descripción del centro es HTML y así es como se inyecta HTML en React */}
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: sc.description,
                                }}
                            />
                        </Box>

                        <Box mt={3}>
                            <Grid
                                container
                                spacing={3}
                                alignItems="center"
                                justifyContent="center"
                            >
                                {/* --- Book --- */}
                                {SCPrivileges.includes(13) &&
                                    sc.modules.find(
                                        (name: any) => name === 'booking',
                                    ) && (
                                        <Grid
                                            size={{
                                                md: hasPurse ? 6 : 4,
                                                xs: 12,
                                            }}
                                        >
                                            <SttButton
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                caption={t('timetable.book')}
                                                startIcon={<Apps />}
                                                onClick={() =>
                                                    navigate(
                                                        getPath(
                                                            'timetable' as any,
                                                            {
                                                                state: {
                                                                    id: sc.id,
                                                                },
                                                            },
                                                        ),
                                                    )
                                                }
                                            />
                                        </Grid>
                                    )}

                                {/* --- Recharge purse --- */}
                                {hasPurse && (
                                    <Grid size={{ md: 6, xs: 12 }}>
                                        <SttButton
                                            fullWidth
                                            caption={t('payment.chargePurse')}
                                            startIcon={
                                                sc.city?.country?.currency !==
                                                    'EUR' &&
                                                sc.city?.country?.currency ? (
                                                    sc.city?.country?.currency
                                                ) : (
                                                    <Euro />
                                                )
                                            }
                                            onClick={() =>
                                                navigate(
                                                    getPath(
                                                        'rechargePurse' as any,
                                                        {
                                                            state: {
                                                                id: sc.id,
                                                            },
                                                        },
                                                    ),
                                                )
                                            }
                                        />
                                    </Grid>
                                )}

                                {/* --- Buy abonos --- */}
                                <Grid size={{ md: hasPurse ? 6 : 4, xs: 12 }}>
                                    <SttButton
                                        fullWidth
                                        caption={t('booking.buyAbonos')}
                                        startIcon={<PostAdd />}
                                        onClick={() =>
                                            navigate(
                                                getPath('abonoList', {
                                                    state: {
                                                        id: sc.id,
                                                    },
                                                }),
                                            )
                                        }
                                    />
                                </Grid>

                                {/* --- Buy bono --- */}
                                <Grid size={{ md: hasPurse ? 6 : 4, xs: 12 }}>
                                    <SttButton
                                        fullWidth
                                        caption={t('booking.bonos')}
                                        startIcon={<BallotOutlined />}
                                        onClick={() =>
                                            navigate(
                                                getPath('bonoList', {
                                                    state: {
                                                        id: sc.id,
                                                    },
                                                }),
                                            )
                                        }
                                    />
                                </Grid>

                                {/* --- Documents --- */}
                                <Grid size={{ md: hasPurse ? 6 : 4, xs: 12 }}>
                                    <SttButton
                                        fullWidth
                                        caption={t('general.documents')}
                                        startIcon={<InsertDriveFileIcon />}
                                        onClick={() =>
                                            navigate(
                                                getPath('scDocuments', {
                                                    state: {
                                                        id: sc.id,
                                                    },
                                                }),
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    {/* Tabs with activities and events. */}
                    <SttTabs
                        fullWidth
                        tabs={[
                            {
                                caption: t('scProfile.wall'),
                                component: <SCWall scId={idSC} />,
                            },
                            {
                                caption: t('activity.activities'),
                                component: (
                                    <>
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <SttValidatedForm
                                                form={formActivity}
                                                fields={[
                                                    {
                                                        type: 'textInput',
                                                        name: 'keyword',
                                                        caption:
                                                            t('common.search'),
                                                        size: 'medium',
                                                        InputProps: {
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        onClick={() => {
                                                                            setSearchKeywordActivity(
                                                                                watchActivityKeyword,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <SearchIcon />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        },
                                                        onEnterKey: (
                                                            value: any,
                                                        ) => {
                                                            setSearchKeywordActivity(
                                                                value,
                                                            );
                                                        },
                                                    },
                                                ]}
                                            />
                                        </Box>
                                        <SttActivityCardList
                                            params={{
                                                keyword: searchKeywordActivity,
                                                idSC: sc?.id,
                                            }}
                                        />
                                    </>
                                ),
                            },
                            {
                                caption: t('event.events'),
                                component: (
                                    <>
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <SttValidatedForm
                                                form={formEvent}
                                                fields={[
                                                    {
                                                        type: 'textInput',
                                                        name: 'keyword',
                                                        caption:
                                                            t('common.search'),
                                                        size: 'medium',
                                                        InputProps: {
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        onClick={() => {
                                                                            setSearchKeywordEvent(
                                                                                watchEventKeyword,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <SearchIcon />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        },
                                                        onEnterKey: (
                                                            value: any,
                                                        ) => {
                                                            setSearchKeywordEvent(
                                                                value,
                                                            );
                                                        },
                                                    },
                                                ]}
                                            />
                                        </Box>
                                        <SttEventCardList
                                            params={{
                                                keyword: searchKeywordEvent,
                                                idSC: sc?.id,
                                            }}
                                        />
                                    </>
                                ),
                            },
                        ]}
                    />
                </Paper>
            </Container>
        );
    }

    if (sc?.status === 'INACTIVE') {
        return <Page404 />;
    }

    return null;
}
